import React from "react";
import { css } from "aphrodite";
import styles from "./styles";
import { Typography, Switch } from "@mui/material";
import { useTranslation } from "react-i18next";
import NumericDynamicField from "./NumericDynamicField";
import { isFieldMandatory } from "../../../../Utils/dynamicFieldValidation";

const DynamicFieldsRenderer = ({
  dynamicFields = [],
  values = {},
  touched = {},
  errors = {},
  onChange,
  onBlur,
}) => {
  const [t, i18n] = useTranslation(["translation"]);

  if (!dynamicFields || dynamicFields.length === 0) {
    return null;
  }

  const getFieldLabel = (field) => field[`label_${i18n.language}`] || field.label || "";

  const renderLabel = (field) => (
    <div className={css(styles.labelContainer)}>
      <Typography variant="body2" className={css(styles.labelText)}>
        {getFieldLabel(field)}
        {isFieldMandatory(field) ? " *" : ""}
      </Typography>
    </div>
  );

  const renderField = (field) => {
    const fieldId = field.dynamicFieldId;
    const value = values[fieldId] || "";
    const isTouched = touched[fieldId];
    const error = errors[fieldId];
    const hasError = isTouched && error;

    const handleChange = (e) => {
      onChange(fieldId, e.target.value, field);
    };

    const handleBlurEvent = (e) => {
      onBlur(fieldId, e.target.value, field);
    };

    // Type 0: Text
    if (field.type === 0) {
      return (
        <div key={fieldId} className={css(styles.container)}>
          {renderLabel(field)}
          <div className={css(styles.numberFieldGroup)}>
            <div
              className={css(
                styles.numberInputWrapper,
                !field.suffix && styles.numberInputWrapperRounded,
                hasError && styles.numberInputError
              )}
            >
              <input
                type="text"
                className={css(styles.numberInput)}
                value={value}
                onChange={handleChange}
                onBlur={handleBlurEvent}
                maxLength={1000}
              />
            </div>
            {field.suffix && (
              <div className={css(styles.suffixAddon, hasError && styles.numberInputError)}>
                <Typography variant="body1">{field.suffix}</Typography>
              </div>
            )}
          </div>
          {hasError && (
            <div className={css(styles.errorField)}>
              <Typography className={css(styles.errorText)} variant="subtitle2">
                {error}
              </Typography>
            </div>
          )}
        </div>
      );
    }

    // Type 1: Int
    if (field.type === 1) {
      return (
        <div key={fieldId} className={css(styles.container)}>
          {renderLabel(field)}
          <NumericDynamicField
            field={field}
            value={value}
            hasError={hasError}
            onChange={(nextValue) => onChange(fieldId, nextValue, field)}
            onBlur={(nextValue) => onBlur(fieldId, nextValue, field)}
          />
          {hasError && (
            <div className={css(styles.errorField)}>
              <Typography className={css(styles.errorText)} variant="subtitle2">
                {error}
              </Typography>
            </div>
          )}
        </div>
      );
    }

    // Type 2: Dec
    if (field.type === 2) {
      return (
        <div key={fieldId} className={css(styles.container)}>
          {renderLabel(field)}
          <NumericDynamicField
            field={field}
            value={value}
            hasError={hasError}
            onChange={(nextValue) => onChange(fieldId, nextValue, field)}
            onBlur={(nextValue) => onBlur(fieldId, nextValue, field)}
          />
          {hasError && (
            <div className={css(styles.errorField)}>
              <Typography className={css(styles.errorText)} variant="subtitle2">
                {error}
              </Typography>
            </div>
          )}
        </div>
      );
    }

    // Type 3: Bit (Switch ON/OFF)
    if (field.type === 3) {
      const isChecked = value === "true";

      const handleSwitchChange = (e) => {
        const nextValue = e.target.checked ? "true" : "false";
        onChange(fieldId, nextValue, field);
      };

      const handleSwitchBlur = () => {
        onBlur(fieldId, value || "false", field);
      };

      return (
        <div key={fieldId} className={css(styles.container)}>
          {renderLabel(field)}
          <div className={css(styles.numberFieldGroup)}>
            <div
              className={css(
                styles.numberInputWrapper,
                !field.suffix && styles.numberInputWrapperRounded,
                hasError && styles.numberInputError,
                styles.switchWrapper
              )}
            >
              <Switch
                checked={isChecked}
                onChange={handleSwitchChange}
                onBlur={handleSwitchBlur}
              />
              <Typography variant="body1" className={css(styles.switchLabel)}>
                {isChecked ? t("Yes") : t("No")}
              </Typography>
            </div>
            {field.suffix && (
              <div className={css(styles.suffixAddon, hasError && styles.numberInputError)}>
                <Typography variant="body1">{field.suffix}</Typography>
              </div>
            )}
          </div>
          {hasError && (
            <div className={css(styles.errorField)}>
              <Typography className={css(styles.errorText)} variant="subtitle2">
                {error}
              </Typography>
            </div>
          )}
        </div>
      );
    }

    return null;
  };

  return (
    <div className={css(styles.dynamicFieldsContainer)}>
      {dynamicFields.map((field) => renderField(field))}
    </div>
  );
};

export default DynamicFieldsRenderer;
